// @ts-nocheck

import BScroll from 'better-scroll';
import React, { useRef, useEffect, useCallback, forwardRef, useState, ReactNode } from 'react';
import ResizeDetector from 'react-resize-detector';
import styled, { css } from 'styled-components';

import { Target } from '../../client';

const Wrapper = styled.div`
  ${({ $withLeftShadow, $withRightShadow }) => css`
    transform-style: preserve-3d;

    ${Target.__MOBILE__
      ? css`
          &:before {
            content: '';
            position: absolute;
            width: 3em;
            height: 100%;
            top: 0;
            left: -0.1em;

            background: linear-gradient(
              to right,
              var(--BScrollViewLeftShadow-backgroundColor, transparent),
              transparent
            );
            transition: opacity 200ms ease-in-out;
            pointer-events: none;
            z-index: 1;
          }

          &:after {
            content: '';
            position: absolute;
            width: 3em;
            height: 100%;
            top: 0;
            right: -0.1em;

            background: linear-gradient(
              to left,
              var(--BScrollViewRightShadow-backgroundColor, transparent),
              transparent
            );

            transition: opacity 200ms ease-in-out;
            pointer-events: none;
            z-index: 1;
          }

          ${!$withLeftShadow
            ? css`
                &:before {
                  opacity: 0;
                }
              `
            : undefined}
          ${!$withRightShadow
            ? css`
                &:after {
                  opacity: 0;
                }
              `
            : undefined}
        `
      : undefined}
  `}
`;

// Fix overflow on sliding animation (ex: betbuilder modal) on IE 10
const ChildrenWrapper = styled.div`
  overflow: hidden;
  min-height: 100%;

  ${({ $contentSpacing, $blockHeight }) => css`
    padding: ${$contentSpacing ? `${$contentSpacing}px` : undefined};
    height: ${$blockHeight ? `100%` : undefined};
  `}
`;

type BScrollViewType = {
  vertical?: boolean;
  children: ReactNode;
  style?: unknown;
  className?: string;
  shouldScrollToElement?: boolean;
  shouldDisplayScrollbar?: boolean;
  elementToScrollTo?: unknown;
  isOverflowing?: boolean;
  contentSpacing?: unknown;
  blockHeight?: boolean;
  offsetX?: boolean;
};

const BScrollView = forwardRef<Ref, BScrollViewType>(function BScrollView(
  {
    vertical = true,
    children,
    style,
    className,
    shouldScrollToElement,
    shouldDisplayScrollbar = !Target.__MOBILE__,
    elementToScrollTo,
    isOverflowing,
    contentSpacing,
    blockHeight = false,
    offsetX = false,
  },
  ref,
) {
  const bscrollNodeRef = useRef();
  const bscrollInstance = useRef();
  const scrollEventListenerRef = useRef();

  const [isScrollPositionAtStart, setIsScrollPositionAtStart] = useState(true);
  const [isScrollPositionAtEnd, setIsScrollPositionAtEnd] = useState(false);

  useEffect(() => {
    let options = null;

    if (vertical) {
      options = {
        preventDefault: false, // to fix betradar widget with better-scroll
        tap: true,
        scrollbar: shouldDisplayScrollbar,
        mouseWheel: !Target.__MOBILE__,
      };
    } else {
      options = {
        tap: true,
        scrollX: true,
        scrollY: false,
        eventPassthrough: 'vertical',
        scrollbar: shouldDisplayScrollbar, // No scrollbar for mobile or scoreboards desktop/mobile
        mouseWheel: true,
        probeType: 3,
      };
    }

    const bscroll = new BScroll(bscrollNodeRef.current, options);
    bscrollInstance.current = bscroll;

    return () => {
      bscroll.destroy();
      bscrollInstance.current = null;
    };
  }, [shouldDisplayScrollbar, vertical]);

  useEffect(() => {
    if (shouldScrollToElement && isOverflowing && bscrollInstance?.current) {
      const bscroll = bscrollInstance.current;
      bscroll.refresh(); // Make sure bscroll is ready before trying to scroll
      bscrollInstance.current.scrollToElement(elementToScrollTo.current || elementToScrollTo, 2000, offsetX);
    }
  }, [elementToScrollTo, isOverflowing, shouldScrollToElement, bscrollInstance, offsetX]);

  // FIXME: In a near future react hooks should be able to handle forwardRef properly.
  useEffect(() => {
    if (ref) {
      ref.current = bscrollNodeRef.current;
    }
  }, [ref]);

  const onResizeHandler = useCallback(() => {
    if (bscrollInstance.current) {
      bscrollInstance.current.refresh();
    }
  }, []);

  useEffect(() => {
    if (Target.__MOBILE__) {
      const wrapperNode = bscrollNodeRef.current;
      const bscroll = bscrollInstance.current;

      if (wrapperNode && bscroll) {
        const scrollEventListener = scrollEventListenerRef.current;

        if (scrollEventListener) {
          bscroll.off('scroll', scrollEventListener);
        }

        const innerNode = wrapperNode?.childNodes[0]?.childNodes[0];

        if (innerNode) {
          const innerNodeWidth = innerNode.getBoundingClientRect()?.width;
          const wrapperNodeWidth = wrapperNode.getBoundingClientRect()?.width;

          if (innerNodeWidth && wrapperNodeWidth) {
            const scrollEventListener = (e) => {
              const { x } = e;
              setIsScrollPositionAtStart(x >= -5);
              setIsScrollPositionAtEnd(x <= -(innerNodeWidth - wrapperNodeWidth) + 5);
            };
            scrollEventListenerRef.current = scrollEventListener;
            bscroll.on('scroll', scrollEventListenerRef.current);
          }
        }
      }
    }
  }, [children]);

  useEffect(() => {
    return () => {
      const scrollEventListener = scrollEventListenerRef.current;
      const bscroll = bscrollInstance.current;

      if (bscroll && scrollEventListener) {
        bscroll.off('scroll', scrollEventListener);
      }
    };
  }, []);

  return (
    <Wrapper
      style={style}
      className={className}
      ref={bscrollNodeRef}
      $withLeftShadow={!vertical && !isScrollPositionAtStart}
      $withRightShadow={!vertical && !isScrollPositionAtEnd}
    >
      {vertical ? (
        <ChildrenWrapper $contentSpacing={contentSpacing} $blockHeight={blockHeight}>
          {children}
          <ResizeDetector handleHeight handleWidth onResize={onResizeHandler} />
        </ChildrenWrapper>
      ) : (
        <>
          {children}
          <ResizeDetector handleHeight handleWidth onResize={onResizeHandler} />
        </>
      )}
    </Wrapper>
  );
});

export default BScrollView;
